import { MODEL_NAMES } from '../../constants/index';
import { TrackedSet } from 'tracked-built-ins';
import { cached, tracked } from '@glimmer/tracking';
import { didCancel, task, timeout } from 'ember-concurrency';
import {
  pageNumberInfinityModel,
  pageNumberInfinityOptions,
} from '../../utils/messaging/infinity-model';
import { service } from '@ember/service';
import MessagingRoutesBaseHandler from './base-handler.js';

export const CONTACT_PAGE_SIZE = 15;
export const CONTACT_ROLE_FILTER = {
  role: ['primary clinician', 'couple', 'primary clinician for contact'],
};

export default class MessagingRoutesContactsHandler extends MessagingRoutesBaseHandler {
  @service alertManager;
  @service infinity;
  @service intl;
  @service messaging;
  @service messagingLogging;
  @service messagingNavigation;

  @tracked search = '';
  @tracked contacts = [];

  selectedContactIds = new TrackedSet();

  /** Map of contact id → contact model. Persists across search so we can resolve
  selected ids to contacts and reuse references. */
  #contactsCache = new Map();

  get groupMessagingEnabled() {
    return this.messaging.options.groupMessagingEnabled;
  }

  get hasSearchTerm() {
    return Boolean(this.search?.trim());
  }

  // Used to know when we shouldn't display "recent" and "related" client headers.
  get lastFetchWasSearch() {
    return Boolean(this.getContacts.lastComplete?.args?.[0]?.search);
  }

  @cached
  get selectableContacts() {
    const contacts = this.contacts;
    if (!contacts || !contacts.length) return [];

    return contacts.content.map(contact => this.#getOrCreateCachedContact(contact));
  }

  get selectedContacts() {
    return Array.from(this.selectedContactIds)
      .map(id => this.#contactsCache.get(id))
      .filter(Boolean);
  }

  @cached
  get selectedParticipantsProperties() {
    const selectedClientExternalIds = [];
    const selectedUserExternalIds = [];

    this.selectedContacts.forEach(contact => {
      if (contact.isClient) {
        selectedClientExternalIds.push(contact.externalId);
      } else {
        selectedUserExternalIds.push(contact.externalId);
      }
    });

    return {
      'selected_client_external_ids': selectedClientExternalIds,
      'selected_user_external_ids': selectedUserExternalIds,
    };
  }

  get hasSelectedClient() {
    return Boolean(this.selectedParticipantsProperties.selected_client_external_ids.length);
  }

  get getOnlyRelatedClients() {
    return this.hasSelectedClient && !this.hasSearchTerm;
  }

  onVisit = task({ drop: true }, async () => {
    await this.searchContacts.perform();
  });

  searchContacts = task({ restartable: true }, async () => {
    const searchValue = this.search?.trim();
    const filter = { ...(searchValue && { search: searchValue }) };

    // Add debounce only for search input (not initial load)
    if (this.search) {
      await timeout(300);
    }

    try {
      await this.getContacts.perform(filter);
    } catch (err) {
      this.handleError(err);
    }
  });

  getContacts = task({ restartable: true }, async (filter = {}) => {
    if (this.getOnlyRelatedClients) {
      filter = { ...filter, related_clients_only: true };
    }

    const model = await this.infinity.model(
      MODEL_NAMES.CONTACT,
      {
        ...(Object.keys(filter).length && { filter }),
        include: 'couple',
        ...this.selectedParticipantsProperties,
        ...pageNumberInfinityOptions(CONTACT_PAGE_SIZE),
      },
      pageNumberInfinityModel(CONTACT_PAGE_SIZE)
    );

    this.contacts = model;
  });

  updateSearch(search) {
    this.search = search;
    this.searchContacts.perform();
  }

  clearSearch() {
    this.updateSearch('');
  }

  loadMoreContacts = task({ drop: true }, async model => {
    if (!model.canLoadMore) return;
    try {
      await this.infinity.loadNextPage(model);
    } catch (err) {
      this.handleError(err);
    }
  });

  handleError(err) {
    if (didCancel(err)) return;
    this.messagingLogging.error(err, { context: 'contacts' });
    this.alertManager.broadcast('toast', {
      message: this.intl.t('emc.general_error'),
      type: 'error',
    });
  }

  selectContact = contact => {
    if (this.groupMessagingEnabled) {
      this.toggleContactSelection(contact);
    } else {
      const conversation = this.messaging.findOrCreateConversationWithContacts(contact.members);
      this.messagingNavigation.toConversation(conversation);
    }
  };

  toggleContactSelection = contact => {
    const id = contact.id;
    if (this.selectedContactIds.has(id)) {
      this.selectedContactIds.delete(id);
    } else {
      this.selectedContactIds.add(id);
      this.clearSearch();
    }
  };

  #getOrCreateCachedContact(contact) {
    this.#contactsCache.set(contact.id, contact);
    return contact;
  }
}
